import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import {
  User,
  Mail,
  Shield,
  Clock,
  ChevronLeft,
  Fingerprint,
  Activity,
  Copy,
  Check,
  Link2,
  Plus,
  Trash2,
  CheckCircle2,
  XCircle,
  Loader2,
  UserPlus,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from '@/hooks/use-toast';

export const Route = createFileRoute('/clients/$clientId')({
  component: ClientUsersComponent,
});

function ClientUsersComponent() {
  const { clientId } = Route.useParams();
  const [copied, setCopied] = useState(false);

  // 1. Fetch Client Details (to get the Organization Name)
  const { data: client, isLoading: isClientLoading } = useQuery({
  queryKey: ['admin', 'clients', clientId],
  queryFn: async () => {
    const response = await api.clients.get(clientId);
    // Extract data if wrapped
    const result = (response && typeof response === 'object' && 'data' in response) 
      ? (response as any).data 
      : response;
      
    return Array.isArray(result) ? result[0] : result;
  },
  enabled: !!clientId,
});

  // 2. Fetch Users
  const { data: users, isLoading: isUsersLoading, error } = useQuery({
    queryKey: ['admin', 'clients', clientId, 'users'],
    queryFn: async () => {
      const response = await api.clients.listClientUsers(clientId);
      return (response && typeof response === 'object' && 'data' in response) 
        ? (response as any).data 
        : response;
    },
  });

  const handleCopyId = () => {
    navigator.clipboard.writeText(clientId);
    setCopied(true);
    toast({
      title: "ID Copied",
      description: "Client UUID saved to clipboard.",
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const isDataReady = !!client?.name && !!users; 
  const isLoading = isClientLoading || isUsersLoading || !isDataReady;

  if (isLoading) return <div className="p-8 text-slate-400 font-mono text-sm animate-pulse">SYNCHRONIZING DIRECTORY...</div>;
  if (error) return <div className="p-8 text-red-400 font-mono text-sm">ERROR: FAILED TO FETCH CLIENT DATA</div>;

  return (
    <div className="min-h-screen bg-[#0B0E14] text-slate-200 p-8 font-sans">
      {/* Breadcrumb / Header */}
      <div className="mb-8">
        <Link 
          to="/clients/clients" 
          className="text-cyan-500 hover:text-cyan-400 text-xs font-bold flex items-center gap-1 mb-4 uppercase tracking-widest transition-colors"
        >
          <ChevronLeft size={14} /> Back to Organizations
        </Link>
        
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight uppercase">
              Authorized Users for <span className="text-cyan-400">{client?.name || 'Loading...'}</span>
            </h1>
          </div>
          
          <div className="flex items-center gap-3 mt-1">
            <div className="bg-[#151921] border border-slate-800 rounded-lg px-3 py-1.5 flex items-center gap-3 group">
              <span className="text-slate-500 font-mono text-[14px] flex items-center gap-2 uppercase tracking-tighter">
                <Fingerprint size={12} className="text-cyan-600" />
                ClientId: <span className="text-slate-300 select-all">{clientId}</span>
              </span>
              
              <button 
                onClick={handleCopyId}
                className="text-slate-500 hover:text-cyan-400 transition-colors p-1"
                title="Copy Client ID"
              >
                {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
              </button>
            </div>
          </div>
        </div>
        <p className="text-slate-500 text-sm mt-4 font-light">
          Manage personnel access levels and authentication logs for this directory.
        </p>
      </div>

      {/* Users Table */}
      <div className="bg-[#151921] rounded-xl border border-slate-800 overflow-hidden shadow-2xl">
        <div className="p-5 border-b border-slate-800 flex items-center justify-between bg-[#1A1F29]/50">
          <h3 className="text-sm font-semibold text-slate-400 tracking-wider uppercase flex items-center gap-2">
            <User size={16} className="text-cyan-500" />
            Active Personnel
          </h3>
          <span className="text-[10px] bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 px-2 py-1 rounded font-bold">
            {users?.length || 0} IDENTIFIED
          </span>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#1A1F29] text-slate-500 uppercase text-[10px] tracking-widest">
              <tr>
                <th className="px-6 py-4 font-medium">User Profile</th>
                <th className="px-6 py-4 font-medium">System Role</th>
                <th className="px-6 py-4 font-medium">Status</th>
                <th className="px-6 py-4 font-medium text-right">Last Authentication</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {users?.map((user: any) => (
                <tr 
                  key={user.id} 
                  className="hover:bg-slate-800/30 transition-colors group"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-[#1A1F29] border border-slate-700 flex items-center justify-center text-cyan-500 font-bold group-hover:border-cyan-500/50 transition-all">
                        {user.firstName?.[0]}{user.lastName?.[0]}
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-slate-100 group-hover:text-cyan-400 transition-colors">
                          {user.firstName} {user.lastName}
                        </span>
                        <span className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                          <Mail size={10} />
                          {user.email}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Shield size={14} className={user.role === 'admin' ? 'text-orange-400' : 'text-slate-500'} />
                      <span className={`text-[11px] font-bold uppercase tracking-tighter ${
                        user.role === 'admin' ? 'text-orange-400' : 'text-slate-300'
                      }`}>
                        {user.role.replace('_', ' ')}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5">
                      <div className={`h-1.5 w-1.5 rounded-full ${user.status === 'active' ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : 'bg-slate-600'}`} />
                      <span className={`text-[12px] uppercase font-bold tracking-widest ${
                        user.status === 'active' ? 'text-green-400' : 'text-slate-500'
                      }`}>
                        {user.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex flex-col items-end">
                      <span className="text-slate-300 text-[12px] font-mono flex items-center gap-2">
                        <Clock size={12} className="text-slate-600" />
                        {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'NEVER'}
                      </span>
                      <span className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-tighter">
                        {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'LOG_MISSING'}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {users?.length === 0 && (
          <div className="p-12 text-center flex flex-col items-center justify-center gap-2 text-slate-600">
            <Activity size={32} className="opacity-20" />
            <p className="text-sm font-mono uppercase tracking-widest">No active user sessions found for this client</p>
          </div>
        )}
      </div>

      {/* Invite Management Section */}
      <InviteSection clientId={clientId} />

      {/* Join Requests Section */}
      <JoinRequestSection clientId={clientId} />
    </div>
  );
}

// =============================================
// Invite Management
// =============================================

function InviteSection({ clientId }: { clientId: string }) {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteDays, setInviteDays] = useState(7);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const { data: invites, isLoading } = useQuery({
    queryKey: ['admin', 'invites', clientId],
    queryFn: () => api.onboarding.listInvites({ clientId }),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      api.onboarding.createInvite({
        clientId,
        role: inviteRole,
        email: inviteEmail || undefined,
        expiresInDays: inviteDays,
      }),
    onSuccess: (data) => {
      toast({ title: 'Invite Created', description: 'Copy the link and share it with the user.' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'invites', clientId] });
      setShowCreate(false);
      setInviteEmail('');
      setInviteRole('viewer');
      // Auto-copy to clipboard
      const link = `${window.location.origin}/invite/${data.token}`;
      navigator.clipboard.writeText(link);
      setCopiedToken(data.token);
      setTimeout(() => setCopiedToken(null), 3000);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => api.onboarding.revokeInvite(id),
    onSuccess: () => {
      toast({ title: 'Invite Revoked' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'invites', clientId] });
    },
  });

  const handleCopyLink = (token: string) => {
    const link = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(link);
    setCopiedToken(token);
    toast({ title: 'Link Copied', description: 'Invite link saved to clipboard.' });
    setTimeout(() => setCopiedToken(null), 3000);
  };

  return (
    <div className="bg-[#151921] rounded-xl border border-slate-800 overflow-hidden shadow-2xl mt-6">
      <div className="p-5 border-b border-slate-800 flex items-center justify-between bg-[#1A1F29]/50">
        <h3 className="text-sm font-semibold text-slate-400 tracking-wider uppercase flex items-center gap-2">
          <Link2 size={16} className="text-purple-400" />
          Invite Links
        </h3>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="text-[10px] bg-purple-500/10 text-purple-400 border border-purple-500/20 px-3 py-1.5 rounded font-bold flex items-center gap-1.5 hover:bg-purple-500/20 transition-colors"
        >
          <Plus size={12} />
          NEW INVITE
        </button>
      </div>

      {/* Create Invite Form */}
      {showCreate && (
        <div className="p-5 border-b border-slate-800 bg-[#1A1F29]/30">
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-[10px] text-slate-500 uppercase tracking-widest mb-1.5 font-medium">
                Role
              </label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-[#0B0E14] border border-slate-700 text-slate-200 text-sm focus:outline-none focus:border-purple-500/50"
              >
                <option value="viewer">Viewer</option>
                <option value="credit_analyst">Credit Analyst</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] text-slate-500 uppercase tracking-widest mb-1.5 font-medium">
                Email (optional)
              </label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="Restrict to email..."
                className="w-full px-3 py-2 rounded-lg bg-[#0B0E14] border border-slate-700 text-slate-200 text-sm placeholder:text-slate-600 focus:outline-none focus:border-purple-500/50"
              />
            </div>
            <div>
              <label className="block text-[10px] text-slate-500 uppercase tracking-widest mb-1.5 font-medium">
                Expires In (days)
              </label>
              <input
                type="number"
                value={inviteDays}
                onChange={(e) => setInviteDays(Number(e.target.value))}
                min={1}
                max={90}
                className="w-full px-3 py-2 rounded-lg bg-[#0B0E14] border border-slate-700 text-slate-200 text-sm focus:outline-none focus:border-purple-500/50"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending}
              className="px-4 py-2 rounded-lg bg-purple-500/20 text-purple-400 border border-purple-500/30 text-sm font-medium hover:bg-purple-500/30 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {createMutation.isPending && <Loader2 size={14} className="animate-spin" />}
              Generate Link
            </button>
            <button
              onClick={() => setShowCreate(false)}
              className="px-4 py-2 rounded-lg text-slate-400 text-sm hover:bg-slate-800 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Invites Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#1A1F29] text-slate-500 uppercase text-[10px] tracking-widest">
            <tr>
              <th className="px-6 py-3 font-medium">Token</th>
              <th className="px-6 py-3 font-medium">Role</th>
              <th className="px-6 py-3 font-medium">Email Restriction</th>
              <th className="px-6 py-3 font-medium">Status</th>
              <th className="px-6 py-3 font-medium">Expires</th>
              <th className="px-6 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {invites?.map((invite: any) => (
              <tr key={invite.id} className="hover:bg-slate-800/30 transition-colors">
                <td className="px-6 py-3 font-mono text-[11px] text-slate-400">
                  {invite.token.substring(0, 8)}...
                </td>
                <td className="px-6 py-3">
                  <span className="text-[10px] font-bold uppercase text-slate-300">
                    {invite.role}
                  </span>
                </td>
                <td className="px-6 py-3 text-[12px] text-slate-400">
                  {invite.email || <span className="text-slate-600">Any</span>}
                </td>
                <td className="px-6 py-3">
                  <InviteStatusBadge status={invite.status} />
                </td>
                <td className="px-6 py-3 text-[12px] text-slate-500 font-mono">
                  {new Date(invite.expiresAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {invite.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleCopyLink(invite.token)}
                          className="p-1.5 rounded text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors"
                          title="Copy invite link"
                        >
                          {copiedToken === invite.token ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                        </button>
                        <button
                          onClick={() => revokeMutation.mutate(invite.id)}
                          className="p-1.5 rounded text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          title="Revoke invite"
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!isLoading && (!invites || invites.length === 0) && (
        <div className="p-8 text-center text-slate-600">
          <Link2 size={24} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm font-mono uppercase tracking-widest">No invites generated yet</p>
        </div>
      )}
    </div>
  );
}

function InviteStatusBadge({ status }: { status: string }) {
  const config: Record<string, { color: string; label: string }> = {
    pending: { color: 'border-amber-500/50 text-amber-400 bg-amber-500/10', label: 'ACTIVE' },
    redeemed: { color: 'border-green-500/50 text-green-400 bg-green-500/10', label: 'REDEEMED' },
    expired: { color: 'border-slate-500/50 text-slate-400 bg-slate-500/10', label: 'EXPIRED' },
    revoked: { color: 'border-red-500/50 text-red-400 bg-red-500/10', label: 'REVOKED' },
  };
  const c = config[status] || config.expired;
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase ${c.color}`}>
      {c.label}
    </span>
  );
}

// =============================================
// Join Request Management
// =============================================

function JoinRequestSection({ clientId }: { clientId: string }) {
  const queryClient = useQueryClient();

  // Fetch all join requests and filter to this client (or unresolved ones)
  const { data: allRequests } = useQuery({
    queryKey: ['admin', 'join-requests'],
    queryFn: () => api.onboarding.listJoinRequests({ status: 'pending' }),
  });

  // Filter to requests matching this client or unresolved ones
  const requests = allRequests?.filter(
    (r: any) => r.clientId === clientId || (!r.clientId && r.status === 'pending')
  );

  const reviewMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      api.onboarding.reviewJoinRequest(id, data),
    onSuccess: (_, variables) => {
      toast({
        title: variables.data.action === 'approve' ? 'Request Approved' : 'Request Denied',
        description: variables.data.action === 'approve' ? 'User has been assigned to this organization.' : 'The join request has been denied.',
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'join-requests'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients', clientId, 'users'] });
    },
  });

  if (!requests || requests.length === 0) return null;

  return (
    <div className="bg-[#151921] rounded-xl border border-slate-800 overflow-hidden shadow-2xl mt-6">
      <div className="p-5 border-b border-slate-800 flex items-center justify-between bg-[#1A1F29]/50">
        <h3 className="text-sm font-semibold text-slate-400 tracking-wider uppercase flex items-center gap-2">
          <UserPlus size={16} className="text-amber-400" />
          Pending Join Requests
        </h3>
        <span className="text-[10px] bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2 py-1 rounded font-bold">
          {requests.length} PENDING
        </span>
      </div>

      <div className="divide-y divide-slate-800">
        {requests.map((req: any) => (
          <div key={req.id} className="p-5 flex items-center justify-between hover:bg-slate-800/20 transition-colors">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-full bg-[#1A1F29] border border-slate-700 flex items-center justify-center text-amber-400 font-bold text-sm">
                {req.userFirstName?.[0]}{req.userLastName?.[0]}
              </div>
              <div>
                <p className="text-sm font-medium text-slate-200">
                  {req.userFirstName} {req.userLastName}
                </p>
                <p className="text-[11px] text-slate-500 flex items-center gap-1">
                  <Mail size={10} />
                  {req.userEmail}
                </p>
                <p className="text-[10px] text-slate-600 mt-0.5">
                  Requested: <span className="text-slate-400">"{req.organizationSlug}"</span>
                  {!req.clientId && <span className="text-amber-400 ml-2">(no slug match)</span>}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() =>
                  reviewMutation.mutate({
                    id: req.id,
                    data: { action: 'approve', clientId, role: 'viewer' },
                  })
                }
                disabled={reviewMutation.isPending}
                className="px-3 py-1.5 rounded-lg bg-green-500/10 text-green-400 border border-green-500/20 text-[11px] font-bold uppercase hover:bg-green-500/20 transition-colors disabled:opacity-50 flex items-center gap-1.5"
              >
                <CheckCircle2 size={12} />
                Approve
              </button>
              <button
                onClick={() =>
                  reviewMutation.mutate({
                    id: req.id,
                    data: { action: 'deny', denyReason: 'Organization not matched' },
                  })
                }
                disabled={reviewMutation.isPending}
                className="px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 text-[11px] font-bold uppercase hover:bg-red-500/20 transition-colors disabled:opacity-50 flex items-center gap-1.5"
              >
                <XCircle size={12} />
                Deny
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}